import React, { useCallback, useContext, useMemo, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { ViewDishContext, IViewDishContext } from '../../viewDishState/viewDishContext';
import { Link } from 'react-router-dom';
import { RequestEntity, EntityStatus } from 'types';
import { useTranslation } from 'react-i18next';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { portionSelectionModes } from 'appConstants/dishes';
import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import { ViewPageBlock } from 'components/ViewPage/ViewPageBlock';
import { ViewPageSection } from 'components/ViewPage/ViewPageSection';
import { ItemList } from 'components/ViewPage//ItemList';
import { CategoryList } from 'components/ViewPage/CategoryList';
import { NoItemsNotification } from 'components/ViewPage/NoItemsNotification';
import { Badge } from 'components/Badge';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import Button from 'components/Button/Button';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { ISelectOption } from 'components/Select/types';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import breadcrumbs from 'images/breadcrumbs.svg';
import style from './index.module.scss';
import { Portions } from './components/Portions';

export const ViewForm: React.FC = () => {
	const { featureFlags } = useContext<IAppContext>(AppContext);
	const {
		setTranslationTab,
		changeTypeOfModalWindow,
		setRestaurantIdForRestaurantProps,
		setRestaurantIdForPortions,
		changeSelectedPortionId,
		dishFullInfo,
		accessibleRestaurants,
		accessiblePortionsRestaurants,
		selectedTranslationTab,
		restaurantIdForRestaurantProps,
		restaurantProps,
		restaurantIdForPortions,
		restaurantPortions,
	} = useContext<IViewDishContext>(ViewDishContext);
	const { t } = useTranslation('form');
	const hasPortions = restaurantProps?.hasPortions;
	const hasSchedulePriority = dishFullInfo?.overrideScheduleAvailability;
	const schedulePriorityValue = hasSchedulePriority ? t('yes') : t('no');
	const listOfRestaurants = useMemo(() => accessibleRestaurants, [accessibleRestaurants]);
	const listOfPortionsRestaurants = useMemo(() => accessiblePortionsRestaurants, [accessiblePortionsRestaurants]);
	const inStop = restaurantProps?.isDisabled;
	const inStopValue = t('inStopLabel');
	const isCommentVisible = restaurantProps?.isCommentVisible;
	const isCommentVisibleValue = t('visibleUncertainLabel');
	const protectFromSync = restaurantProps?.isTaxPercentProtectedFromSync;
	const protectFromSyncValue = t('protectFromSyncLabel');
	const isFiscalized = restaurantProps?.isFiscalized;
	const isFiscalizedValue = t('isFiscalizedLabel');
	const portionSelectionMode = portionSelectionModes.find(
		(mode) => mode.id === restaurantProps?.portionSelectionMode,
	);
	const portionSelectionModeValue = portionSelectionMode ? t(portionSelectionMode.name) : '-';
	const listOfCategories = useMemo(() => restaurantProps?.categories || [], [restaurantProps?.categories]);
	const listOfModGroups = useMemo(
		() =>
			restaurantProps?.modifierGroups
				? restaurantProps.modifierGroups.map((modifierGroup) => ({
						id: modifierGroup.id,
						name: modifierGroup.name || '',
				  }))
				: [],
		[restaurantProps?.modifierGroups],
	);
	const listOfSuggested = useMemo(() => dishFullInfo?.suggestedDishes || [], [dishFullInfo?.suggestedDishes]);
	const listOfAdditional = useMemo(() => dishFullInfo?.additionalDishes || [], [dishFullInfo?.additionalDishes]);
	const listOfDishModification = useMemo(
		() => dishFullInfo?.dishModifications || [],
		[dishFullInfo?.dishModifications],
	);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	const mappedRestaurantOptions = useMemo<ISelectOption[]>(() => {
		const visibleItems = showArchived
			? accessibleRestaurants
			: accessibleRestaurants.filter(
					(restaurant) =>
						restaurant.status === EntityStatus.Active || restaurant.id === restaurantIdForRestaurantProps,
			  );

		return visibleItems.map((restaurant) => ({
			...restaurant,
			additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [accessibleRestaurants, showArchived, restaurantIdForRestaurantProps]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	const handleClickAssignRestaurant = useCallback(
		() => changeTypeOfModalWindow('assignRestaurants'),
		[changeTypeOfModalWindow],
	);

	const handleClickAssignSuggested = useCallback(
		() => changeTypeOfModalWindow('assignSuggested'),
		[changeTypeOfModalWindow],
	);

	const handleClickAssignAdditional = useCallback(
		() => changeTypeOfModalWindow('assignAdditional'),
		[changeTypeOfModalWindow],
	);

	const handleClickAssignModifications = useCallback(
		() => changeTypeOfModalWindow('assignModifications'),
		[changeTypeOfModalWindow],
	);

	const handleClickAssignSubcategories = useCallback(
		() => changeTypeOfModalWindow('assignSubcategories'),
		[changeTypeOfModalWindow],
	);

	const handleClickEditModGroupTemplate = useCallback(
		() => changeTypeOfModalWindow('editModGroupTemplate'),
		[changeTypeOfModalWindow],
	);

	const handleClickAssignPortionRestaurant = useCallback(
		() => changeTypeOfModalWindow('assignPortionsRestaurants'),
		[changeTypeOfModalWindow],
	);

	const handleClickCreatePortion = useCallback(
		() => changeTypeOfModalWindow('createPortion'),
		[changeTypeOfModalWindow],
	);

	const handleClickViewPortion = useCallback(
		(value: string) => {
			changeSelectedPortionId(value);
			changeTypeOfModalWindow('viewPortion');
		},
		[changeTypeOfModalWindow],
	);

	const handleClickEditPortion = useCallback(
		(value: string) => {
			changeSelectedPortionId(value);
			changeTypeOfModalWindow('editPortion');
		},
		[changeTypeOfModalWindow],
	);

	const handleClickDeletePortion = useCallback(
		(value: string) => {
			changeSelectedPortionId(value);
			changeTypeOfModalWindow('toConfirmDeletePortion');
		},
		[changeTypeOfModalWindow],
	);

	const renderBadges = useMemo(() => {
		if (dishFullInfo?.type || dishFullInfo?.isCreatedOnPos) {
			return (
				<div className={style.badgesWrapper}>
					{dishFullInfo.type && (
						<Badge color="red">
							<Text
								translationNameSpace="form"
								translationKey={dishFullInfo.type}
								asFragment={true}
								data-test="dishAsModifier"
							/>
						</Badge>
					)}
					{dishFullInfo.isCreatedOnPos ? (
						<Badge color="purple">
							<Text
								translationKey="POS"
								asFragment={true}
								data-test={`isCreatedOnPos=${dishFullInfo.isCreatedOnPos}`}
							/>
						</Badge>
					) : (
						<Badge color="purple">
							<Text
								translationKey="AMS"
								asFragment={true}
								data-test={`isCreatedOnPos=${dishFullInfo.isCreatedOnPos}`}
							/>
						</Badge>
					)}
				</div>
			);
		}
	}, [dishFullInfo?.type, dishFullInfo?.isCreatedOnPos]);

	return (
		<div className={style.container} data-test="ViewForm">
			<div className={style.content}>
				<ViewPageBlock heading="generalPropertiesHeading">
					<ViewPageSection heading="mainInfoHeading">
						<div className={style.row}>
							<div className={style.imgWrapper}>
								{dishFullInfo?.id && dishFullInfo.photoPath ? (
									<ImgUploaderByEntityId
										resource={RequestEntity.DishesMenuV2}
										entityID={dishFullInfo.id}
										isMenuV2={true}
										infoContent={renderBadges}
										photoSize="Medium"
										loaderSize="big"
										key="1"
									/>
								) : (
									<ImgUploaderByEntityId
										resource={RequestEntity.DishesMenuV2}
										entityID=""
										isMenuV2={true}
										infoContent={renderBadges}
										photoSize="Medium"
										loaderSize="big"
										key="2"
									/>
								)}
							</div>
							<div className={style.mainInfoInputs}>
								{dishFullInfo?.type === 'DishAndModifier' && (
									<Text
										translationNameSpace="form"
										translationKey="warningDishAsModifierChange"
										theme="secondary"
										size="s"
										display="inline"
									/>
								)}
								<FullInfoField label="dishNameLabel" value={dishFullInfo?.name || '-'} />
								<FullInfoField label="customerLabel" value={dishFullInfo?.customer.name || '-'} />
								<FullInfoField label="articleNumberLabel" value={dishFullInfo?.articleNumber || '-'} />
							</div>
						</div>
					</ViewPageSection>

					<ViewPageSection heading="descriptionHeading">
						<FullInfoField
							label="descriptionLabel"
							value={dishFullInfo?.description}
							withXSSSecurity={true}
						/>
					</ViewPageSection>

					<ViewPageSection heading="posNameHeading">
						<FullInfoField label="posNameLabel" value={dishFullInfo?.posName || '-'} />
					</ViewPageSection>

					<ViewPageSection heading="idHeading">
						<FullInfoField label="externalIdLabel" value={dishFullInfo?.externalId || '-'} />
					</ViewPageSection>

					<ViewPageSection heading="translationsHeading">
						<MenuTranslationForm
							className={style.translationForm}
							setTranslationTab={setTranslationTab}
							isShowLanguageName={false}
							IDSelectedCustomer={dishFullInfo?.customer.id || ''}
							translations={dishFullInfo?.translations || []}
							selectedTranslationTab={selectedTranslationTab}
							nameLabel="dishNameLabel"
							hasDescriptionFullInfoField={true}
						/>
					</ViewPageSection>

					<ViewPageSection heading="scheduleHeading">
						<FullInfoField
							label="hasSchedulePriorityLabel"
							value={schedulePriorityValue}
							prefixIcon="asCheckbox"
							prefixIconName={hasSchedulePriority ? 'checkMark' : 'crossMark'}
							prefixIconIsChecked={hasSchedulePriority}
						/>
						<div className={style.row}>
							<FullInfoField
								label="activationScheduleLabel"
								value={dishFullInfo?.activationCron || '-'}
							/>
							<FullInfoField
								label="deactivationScheduleLabel"
								value={dishFullInfo?.deactivationCron || '-'}
							/>
						</div>
					</ViewPageSection>

					<ViewPageSection heading="restaurantsHeading">
						{listOfRestaurants.length ? (
							<ItemList
								itemListLabel="allRestaurantsWithThisDish"
								items={listOfRestaurants}
								pathToItem="/clients/restaurants?restaurantFullInfoID="
								assignBtnTranslationKey="editRestaurantsList"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						) : (
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignDishesToCustomersSubcategoryToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						)}
					</ViewPageSection>

					<ViewPageSection heading="suggestedDishesHeading">
						{!!listOfSuggested.length && (
							<ItemList
								items={listOfSuggested}
								pathToItem="/menu-v2/dishes/edit?dishID="
								assignBtnTranslationKey="editListButton"
								onClickShowAssignModal={handleClickAssignSuggested}
							/>
						)}
						{!listOfSuggested.length && (
							<NoItemsNotification
								title="noDishesAssigned"
								description="assignSuggestedDishesToDishToBeAbleToEditThem"
								assignBtnTranslationKey="assignButton"
								onClickShowAssignModal={handleClickAssignSuggested}
							/>
						)}
					</ViewPageSection>

					<ViewPageSection heading="additionalHeading">
						{!!listOfAdditional.length && (
							<ItemList
								items={listOfAdditional}
								pathToItem="/menu-v2/dishes/edit?dishID="
								assignBtnTranslationKey="editListButton"
								onClickShowAssignModal={handleClickAssignAdditional}
							/>
						)}
						{!listOfAdditional.length && (
							<NoItemsNotification
								title="noDishesAssigned"
								description="assignAdditionalIngredientsToDishToBeAbleToEditThem"
								assignBtnTranslationKey="assignButton"
								onClickShowAssignModal={handleClickAssignAdditional}
							/>
						)}
					</ViewPageSection>

					<ViewPageSection heading="dishModificationsHeading">
						{!!listOfDishModification.length && (
							<ItemList
								items={listOfDishModification}
								pathToItem="/menu-v2/dishes/edit?dishID="
								assignBtnTranslationKey="editListButton"
								onClickShowAssignModal={handleClickAssignModifications}
							/>
						)}
						{!listOfDishModification.length && (
							<NoItemsNotification
								title="noDishesAssigned"
								description="assignDishModificationToDishToBeAbleToEditThem"
								assignBtnTranslationKey="assignButton"
								onClickShowAssignModal={handleClickAssignModifications}
							/>
						)}
					</ViewPageSection>

					<div className={style.btnWrapper} data-test="BtnBlock1">
						{/* <Button theme="primaryBlue" disabled={true} onClick={() => console.log('Check tree view')}>
							<Text translationNameSpace="form" translationKey="checkTreeViewButton" asFragment={true} />
						</Button> */}
						<Link
							target={`blank_${Date.now()}`}
							rel="noopener noreferrer"
							to={`/menu-v2/dishes/edit?dishID=${dishFullInfo?.id}`}>
							<Button
								className={style.btn}
								theme="primaryBlue"
								icon={
									<svg className={style.icon}>
										<use xlinkHref={`${breadcrumbs}#pencil`} />
									</svg>
								}>
								<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
							</Button>
						</Link>
					</div>
				</ViewPageBlock>

				<ViewPageBlock heading="restaurantPropertiesHeading">
					{accessibleRestaurants.length ? (
						<>
							<OutsideFormSelect
								testId="restaurantProperties"
								label="restaurantsLabel"
								name="restaurantProperties"
								value={restaurantIdForRestaurantProps}
								options={mappedRestaurantOptions}
								onChange={setRestaurantIdForRestaurantProps}
								keepSearchWithDropdownRender={true}
								dropdownRender={renderSelectMenu}
							/>

							<ViewPageSection heading="categoryAndSubcategoryHeading">
								{restaurantIdForRestaurantProps && !!listOfCategories.length && (
									<CategoryList
										items={listOfCategories}
										onClickShowAssignModal={handleClickAssignSubcategories}
									/>
								)}
								{restaurantIdForRestaurantProps && !listOfCategories.length && (
									<NoItemsNotification
										title="noCategoriesAssigned"
										description="assignCategoriesToCustomersToBeAbleToEditThem"
										assignBtnTranslationKey="assignCategoriesButton"
										onClickShowAssignModal={handleClickAssignSubcategories}
									/>
								)}
								{!restaurantIdForRestaurantProps && (
									<Text translationNameSpace="form" translationKey="-" />
								)}
							</ViewPageSection>

							<ViewPageSection heading="scheduleHeading">
								<div className={style.row}>
									<FullInfoField
										containerClassName={style.marginTopZero}
										label="activationScheduleLabel"
										value={restaurantProps?.activationCron || '-'}
									/>
									<FullInfoField
										containerClassName={style.marginTopZero}
										label="deactivationScheduleLabel"
										value={restaurantProps?.deactivationCron || '-'}
									/>
								</div>
							</ViewPageSection>

							<ViewPageSection heading="pricingStrategyHeading">
								<FullInfoField
									containerClassName={style.marginTopZero}
									label="portionSelectionModeLabel"
									value={portionSelectionModeValue}
								/>
							</ViewPageSection>

							<div className={style.row}>
								<ViewPageSection heading="inStopHeading">
									{restaurantProps ? (
										<FullInfoField
											label="inStopLabel"
											value={inStopValue}
											prefixIcon="asCheckbox"
											prefixIconName={inStop ? 'checkMark' : 'crossMark'}
											prefixIconIsChecked={inStop}
										/>
									) : (
										<FullInfoField label="inStopLabel" value="-" />
									)}
								</ViewPageSection>
								<ViewPageSection heading="commentsHeading">
									{restaurantProps ? (
										<FullInfoField
											label="hideCommentsLabel"
											value={isCommentVisibleValue}
											prefixIcon="asCheckbox"
											prefixIconName={isCommentVisible ? 'checkMark' : 'crossMark'}
											prefixIconIsChecked={isCommentVisible}
										/>
									) : (
										<FullInfoField label="hideCommentsLabel" value="-" />
									)}
								</ViewPageSection>
							</div>

							<ViewPageSection heading="priceHeading">
								<div className={hasPortions ? style.column : style.row}>
									{hasPortions ? (
										<Text
											translationNameSpace="form"
											translationKey="warningPriceHiddenByPortions"
											theme="secondary"
											size="s"
											display="inline"
										/>
									) : (
										<>
											<FullInfoField
												containerClassName={style.marginTopZero}
												label="signedPriceLabel"
												value={
													typeof restaurantProps?.price === 'number'
														? `${restaurantProps.currencySymbol || ''} ${
																restaurantProps.price || 0
														  }`
														: '-'
												}
											/>
											<FullInfoField
												containerClassName={style.marginTopZero}
												label="openPricePercentLabel"
												value={restaurantProps?.openPrice || '-'}
											/>
											<FullInfoField
												containerClassName={style.marginTopZero}
												label="openPriceMaxThresholdLabel"
												value={restaurantProps?.openPriceMaxThreshold || '-'}
											/>
										</>
									)}
								</div>
							</ViewPageSection>

							<div className={style.row}>
								<ViewPageSection heading="taxRateHeading">
									{restaurantProps ? (
										<FullInfoField
											label="protectFromSyncLabel"
											value={protectFromSyncValue}
											prefixIcon="asCheckbox"
											prefixIconName={protectFromSync ? 'checkMark' : 'crossMark'}
											prefixIconIsChecked={protectFromSync}
										/>
									) : (
										<FullInfoField label="protectFromSyncLabel" value="-" />
									)}
									<FullInfoField
										label="taxRateLabel"
										value={
											typeof restaurantProps?.taxPercent === 'number'
												? `${restaurantProps?.taxPercent}%`
												: '-'
										}
									/>
								</ViewPageSection>
								<ViewPageSection heading="feeFiscalisationHeading">
									{restaurantProps ? (
										<FullInfoField
											label="isFiscalizedLabel"
											value={isFiscalizedValue}
											prefixIcon="asCheckbox"
											prefixIconName={isFiscalized ? 'checkMark' : 'crossMark'}
											prefixIconIsChecked={isFiscalized}
										/>
									) : (
										<FullInfoField label="isFiscalizedLabel" value="-" />
									)}
								</ViewPageSection>
							</div>

							<ViewPageSection heading="modifierGroupsHeading">
								{restaurantIdForRestaurantProps ? (
									<>
										{!restaurantProps?.hasPortionBasedModGroups && !!listOfModGroups.length && (
											<ItemList
												items={listOfModGroups}
												pathToItem="/menu-v2/modifier-groups/view?modifierGroupID="
												assignBtnTranslationKey="modGroupTemplateButton"
												onClickShowAssignModal={handleClickEditModGroupTemplate}
											/>
										)}
										{!restaurantProps?.hasPortionBasedModGroups && !listOfModGroups.length && (
											<NoItemsNotification
												title="noModifierGroupsAssigned"
												description="assignModifierGroupsToDishToBeAbleToEditThem"
												assignBtnTranslationKey="modGroupTemplateButton"
												onClickShowAssignModal={handleClickEditModGroupTemplate}
												iconId="pencil"
											/>
										)}
										{!!restaurantProps?.hasPortionBasedModGroups && (
											<Text
												translationNameSpace="form"
												translationKey="warningModGroupsHiddenByPortions"
												theme="secondary"
												size="s"
												display="inline"
											/>
										)}
									</>
								) : (
									<Text translationNameSpace="form" translationKey="-" />
								)}
							</ViewPageSection>
						</>
					) : (
						<ViewPageSection heading="restaurantsHeading">
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerSubcategoryToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						</ViewPageSection>
					)}

					{restaurantIdForRestaurantProps && (
						<div className={style.btnWrapper} data-test="BtnBlock2">
							<Link
								target={`blank_${Date.now()}`}
								rel="noopener noreferrer"
								to={`/menu-v2/dishes/edit-restaurant-properties?dishID=${dishFullInfo?.id || ''}`}>
								<Button
									className={style.btn}
									theme="primaryBlue"
									icon={
										<svg className={style.icon}>
											<use xlinkHref={`${breadcrumbs}#pencil`} />
										</svg>
									}>
									<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
								</Button>
							</Link>
						</div>
					)}
				</ViewPageBlock>

				{featureFlags.HAS_PORTION_IN_MENU_V2 && (
					<ViewPageBlock heading="portionsHeading">
						{listOfPortionsRestaurants.length ? (
							<>
								{/*TODO: hidden by KMD-9332*/}
								{/*<ViewPageSection heading="restaurantsHeading">*/}
								{/*<ItemList*/}
								{/*	itemListLabel="allRestaurantsWithPortionsOfDish"*/}
								{/*	items={listOfPortionsRestaurants}*/}
								{/*	pathToItem="/clients/restaurants?restaurantFullInfoID="*/}
								{/*	assignBtnTranslationKey="editRestaurantsList"*/}
								{/*	onClickShowAssignModal={handleClickAssignPortionRestaurant}*/}
								{/*/>*/}
								<OutsideFormSelect
									testId="restaurantPortions"
									label="restaurantsLabel"
									name="portionsRestaurantProperties"
									value={restaurantIdForPortions}
									options={accessiblePortionsRestaurants}
									onChange={setRestaurantIdForPortions}
								/>
								<Portions
									data={restaurantPortions ?? []}
									onClickInfo={handleClickViewPortion}
									onClickEdit={handleClickEditPortion}
									onClickDelete={handleClickDeletePortion}
								/>
								<div className={style.changePortionPositionWrapper}>
									<Link to="/menu-v2/dishes/portion-positions" data-test="ChangePositionBtn">
										<Button
											theme="primaryBlue"
											icon={
												<svg className={style.icon}>
													<use xlinkHref={`${breadcrumbs}#changePosition`} />
												</svg>
											}>
											<Text
												translationNameSpace="listing"
												translationKey="changePositionButton"
												asFragment={true}
											/>
										</Button>
									</Link>
								</div>
								{/*TODO: hidden by KMD-9332*/}
								{/*</ViewPageSection>*/}
								{/*<ViewPageSection heading="restaurantPropertiesHeading">*/}
								{/*</ViewPageSection>*/}
							</>
						) : (
							<>
								<Text size="s" translationNameSpace="form" translationKey="noPortionsCreated" />
								<FullInfoField containerClassName={style.marginTopZero} label="noDishPortions" />
							</>
						)}
						<div className={style.btnWrapper}>
							<Button
								className={style.btn}
								theme="primaryBlue"
								onClick={handleClickCreatePortion}
								icon={
									<svg className={style.icon}>
										<use xlinkHref={`${breadcrumbs}#plus`} />
									</svg>
								}>
								<Text translationNameSpace="form" translationKey="addPortionButton" asFragment={true} />
							</Button>
						</div>
					</ViewPageBlock>
				)}
			</div>
		</div>
	);
};
